import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Space, Tree, Checkbox, Empty, Spin } from 'antd';
import MasterImageService from 'services/master/image';
import MasterFolderService from 'services/master/folder';

const imageService = new MasterImageService();
const folderService = new MasterFolderService();

const buildTree = (folders = [], parentId = 'root') => {
  const children = folders.filter((item) => (item.parent_id || 'root') === parentId);
  return children.map((item) => ({
    title: item.name,
    key: item.id,
    children: buildTree(folders, item.id),
  }));
};

const MasterImagesModal = ({ open, onCancel, onSelect }) => {
  const [folder, setFolder] = useState('root');
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [selectedIDs, setSelectedIDs] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const uploadInputRef = useRef(null);

  const treeData = useMemo(() => ([
    { title: 'Thư mục gốc', key: 'root', children: buildTree(folders, 'root') },
    { title: 'Tất cả', key: 'all', children: [] },
  ]), [folders]);

  const loadFolders = async () => {
    const data = await folderService.list();
    setFolders(Array.isArray(data) ? data : []);
  };

  const loadImages = async (folderID) => {
    const data = await imageService.list(folderID || 'root');
    setImages(Array.isArray(data) ? data : []);
  };

  const refresh = async (folderID = folder) => {
    setLoading(true);
    try {
      await Promise.all([loadFolders(), loadImages(folderID)]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelectedIDs([]);
    refresh(folder);
  }, [open]);

  const onUploadFiles = async (event) => {
    const files = Array.from(event?.target?.files || []);
    if (!files.length) return;

    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        formData.append('folder', folder || 'root');
        await imageService.upload(formData);
      }
      await loadImages(folder);
    } finally {
      setUploading(false);
      setInputKey((prev) => prev + 1);
    }
  };

  const createFolder = async () => {
    const name = String(newFolderName || '').trim();
    if (!name) return;
    await folderService.create({ name, parent_id: folder || 'root' });
    setNewFolderName('');
    await loadFolders();
  };

  const confirmSelect = () => {
    const selected = images.filter((item) => selectedIDs.includes(item.id));
    onSelect(selected);
    onCancel();
  };

  return (
    <Modal
      title='Thư viện ảnh (Master)'
      open={open}
      onCancel={onCancel}
      width={980}
      onOk={confirmSelect}
      okText='Chọn ảnh'
      cancelText='Đóng'
    >
      <Spin spinning={loading || uploading}>
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16 }}>
          <div>
            <Space direction='vertical' style={{ width: '100%' }}>
              <Tree
                selectedKeys={[folder]}
                defaultExpandedKeys={['root']}
                treeData={treeData}
                onSelect={(keys) => {
                  const selected = keys?.[0] || 'root';
                  setFolder(selected);
                  loadImages(selected);
                }}
              />
              <Input
                placeholder='Tên thư mục mới'
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onPressEnter={createFolder}
              />
              <Button onClick={createFolder}>Tạo thư mục</Button>
            </Space>
          </div>
          <div>
            <Space style={{ marginBottom: 12 }}>
              <Button onClick={() => uploadInputRef.current?.click()}>Upload ảnh</Button>
              <input
                key={inputKey}
                ref={uploadInputRef}
                type='file'
                accept='image/*'
                multiple
                style={{ display: 'none' }}
                onChange={onUploadFiles}
              />
            </Space>

            {images.length === 0 ? (
              <Empty description='Chưa có hình ảnh' />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12, maxHeight: 460, overflow: 'auto' }}>
                {images.map((img) => (
                  <div key={img.id} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8 }}>
                    <img src={img.sm_url || img.url} alt='' style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 6 }} />
                    <div style={{ marginTop: 8 }}>
                      <Checkbox
                        checked={selectedIDs.includes(img.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIDs((prev) => [...prev, img.id]);
                            return;
                          }
                          setSelectedIDs((prev) => prev.filter((id) => id !== img.id));
                        }}
                      >
                        Chọn
                      </Checkbox>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default MasterImagesModal;
